import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart3, Bell, BellOff, Clock, Mail, Send, Zap } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Button, Card, cx, Skeleton } from "@/components/ui";
import { api, ApiError, type NotificationMode, type NotificationSettings, type NotificationTopic } from "@/lib/api";

import { t, msg } from "@/lib/i18n";
const MODES: { value: NotificationMode; label: string; Icon: typeof Bell; hint: string }[] = [
  { value: "instant", label: msg("Sofort"), Icon: Zap, hint: msg("E-Mail, sobald etwas Neues anliegt (höchstens stündlich je Thema)") },
  { value: "daily", label: msg("Täglich"), Icon: Clock, hint: msg("in der Zusammenfassung zur gewählten Uhrzeit") },
  { value: "off", label: msg("Aus"), Icon: BellOff, hint: msg("keine E-Mail – steht weiter im Handlungsbedarf") },
];
const TOPIC_ORDER: NotificationTopic[] = ["critical", "orders", "stock", "inbounds", "returns"];

/** Eigene Benachrichtigungen (jede Person für sich, je Kunde) */
export function NotificationsPage() {
  const { tenantId, tenant } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["notifications", tenantId], queryFn: () => api.notifications.get(tenantId) });
  const save = useMutation({
    mutationFn: (s: NotificationSettings) => api.notifications.save(tenantId, s),
    onMutate: (s) => qc.setQueryData(["notifications", tenantId], (old: typeof q.data) => (old ? { ...old, settings: s } : old)),
    onSuccess: () => toast.success("Gespeichert"),
    onError: () => { toast.error("Speichern fehlgeschlagen"); void qc.invalidateQueries({ queryKey: ["notifications", tenantId] }); },
  });
  const test = useMutation({
    mutationFn: (kind: "daily" | "weekly") => api.notifications.test(tenantId, kind),
    onSuccess: (r) => toast.success(r.sent ? `Beispiel an ${q.data?.email} gesendet` : "Nichts zu senden"),
    onError: (e) => toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Senden fehlgeschlagen") : "Senden fehlgeschlagen"),
  });
  const d = q.data;
  const s = d?.settings;
  const set = (patch: Partial<NotificationSettings>) => s && save.mutate({ ...s, ...patch });
  const setTopic = (k: NotificationTopic, v: NotificationMode) => s && save.mutate({ ...s, topics: { ...s.topics, [k]: v } });

  return (
    <div className="pb-16">
      <PageHeader title={t("Benachrichtigungen")} description={<>{t("Was ihr per E-Mail bekommt – gilt nur für dich")}{d ? <> ({d.email})</> : null} {t("bei")} {tenant.name}.</>} />
      <div className="px-4 md:px-8 space-y-5 max-w-3xl">
        {d && !d.active && !d.staff && (
          <div className="rounded-xl border border-info/25 bg-info-soft/60 px-4 py-3 text-[13px]">{t("E-Mail-Benachrichtigungen werden in Kürze von Logiful freigeschaltet. Deine Einstellungen werden schon gespeichert und gelten ab dann – das Beispiel kannst du dir jetzt schon schicken.")}</div>
        )}
        {d?.staff && (
          <div className="rounded-xl border border-info/25 bg-info-soft/60 px-4 py-3 text-[13px]">{t("Als Logiful-Mitarbeiter bekommst du keine Kunden-Benachrichtigungen – die Einstellungen hier gelten für Kunden-Konten.")}</div>
        )}
        {!s ? (
          <Card className="p-5 space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</Card>
        ) : (
          <>
            <Card>
              <div className="px-5 pt-4 pb-2">
                <h2 className="font-semibold flex items-center gap-2"><Bell className="size-4 text-fg-subtle" /> {t("Themen")}</h2>
                <p className="text-[12.5px] text-fg-muted mt-0.5">{t("„Sofort“ meldet nur Neues – nichts doppelt, höchstens stündlich je Thema. Alles steht zusätzlich im Handlungsbedarf.")}</p>
              </div>
              <ul className="divide-y divide-border">
                {TOPIC_ORDER.map((k) => (
                  <li key={k} className="px-5 py-3 flex flex-wrap items-center gap-3">
                    <div className="min-w-0 flex-1 basis-60">
                      <p className="font-medium text-[13.5px]">{t(d.topics[k].label)}</p>
                      <p className="text-[12px] text-fg-muted">{t(d.topics[k].description)}</p>
                    </div>
                    <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-border" role="radiogroup" aria-label={t(d.topics[k].label)}>
                      {MODES.map((m) => (
                        <button key={m.value} role="radio" aria-checked={s.topics[k] === m.value} title={t(m.hint)} onClick={() => setTopic(k, m.value)} className={cx("h-7 px-2.5 rounded-md text-[12.5px] font-medium inline-flex items-center gap-1.5", s.topics[k] === m.value ? "bg-surface shadow-sm text-fg" : "text-fg-muted hover:text-fg")}>
                          <m.Icon className="size-3.5" /> {t(m.label)}
                        </button>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="p-5 space-y-4">
              <h2 className="font-semibold flex items-center gap-2"><Mail className="size-4 text-fg-subtle" /> {t("Zusammenfassung & Wochenbericht")}</h2>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-[13px]">
                <label className="inline-flex items-center gap-2">
                  {t("Tägliche Zusammenfassung um")}
                  <select value={s.dailyHour} onChange={(e) => set({ dailyHour: Number(e.target.value) })} className="h-8 rounded-lg border border-border bg-surface px-2 tabular-nums">
                    {Array.from({ length: 16 }, (_, i) => i + 5).map((h) => <option key={h} value={h}>{String(h).padStart(2, "0")}{t(":00 Uhr")}</option>)}
                  </select>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={s.weekdaysOnly} onChange={(e) => set({ weekdaysOnly: e.target.checked })} className="accent-ink" /> {t("nur werktags")}
                </label>
              </div>
              <p className="text-[12px] text-fg-subtle -mt-2">{t("Enthält alle offenen Punkte der Themen „Sofort“ und „Täglich“. Ist nichts offen, kommt keine E-Mail.")}</p>
              <label className="flex items-start gap-2 text-[13px] cursor-pointer">
                <input type="checkbox" checked={s.weekly} onChange={(e) => set({ weekly: e.target.checked })} className="accent-ink mt-0.5" />
                <span>
                  <span className="font-medium inline-flex items-center gap-1.5"><BarChart3 className="size-3.5" /> {t("Wochenbericht montags")}</span>
                  <span className="block text-[12px] text-fg-muted">{t("Aufträge, Versand (Anteil in 24 h), verkaufte Einheiten, Retouren und Topseller der Vorwoche – mit Vergleich.")}</span>
                </span>
              </label>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" icon={<Send className="size-3.5" />} loading={test.isPending && test.variables === "daily"} onClick={() => test.mutate("daily")} disabled={d.staff}>{t("Beispiel: Zusammenfassung")}</Button>
                <Button size="sm" icon={<Send className="size-3.5" />} loading={test.isPending && test.variables === "weekly"} onClick={() => test.mutate("weekly")} disabled={d.staff}>{t("Beispiel: Wochenbericht")}</Button>
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
